import { useCallback, useState } from 'react';
import ls from 'local-storage';

export const useScenarioState = (currentUser, initialTags = []) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [playerName, setPlayerName] = useState(currentUser?.characterName || null);
  const [scenarioIdea, setScenarioIdea] = useState(null);
  const [charactersNames, setCharactersNames] = useState([]);
  const [playerDescription, setPlayerDescription] = useState(currentUser?.characterDescription || null);
  const [charactersDescription, setCharactersDescription] = useState({});
  const [charactersAvatars, setCharactersAvatars] = useState({});
  const [scenarioName, setScenarioName] = useState(null);
  const [setup, setSetup] = useState(null);
  const [location, setLocation] = useState(null);
  const [initialMessages, setInitialMessages] = useState([]);
  const [selectedTags, setSelectedTags] = useState(initialTags);
  const [picturesGenerated, setPicturesGenerated] = useState(0);

  const saveDataToStorage = useCallback((step, last = false) => {
    const stepData = [
      { condition: step > 0, data: { playerName, scenarioIdea, selectedTags } },
      { condition: step > 1, data: { charactersNames } },
      { condition: step > 2, data: { playerDescription, charactersDescription, charactersAvatars } },
      { condition: step > 3, data: { setup } },
      { condition: step > 4, data: { location } },
      { condition: step > 5, data: { initialMessages } },
      { condition: step > 6 || last, data: { scenarioName } }
    ];

    const data = stepData
      .filter(item => item.condition)
      .reduce((result, item) => ({ ...result, ...item.data }), {});

    ls.set('scenario_wizard_data', data);
    ls.set('scenario_wizard_step', step);
  }, [playerName, scenarioIdea, selectedTags, charactersNames, playerDescription, charactersDescription, charactersAvatars, setup, location, initialMessages, scenarioName]);

  const setDataFromStorage = useCallback(() => {
    const data = ls.get('scenario_wizard_data');
    const step = ls.get('scenario_wizard_step');

    const stepSetters = [
      {
        condition: step > 0,
        action: () => {
          setPlayerName(data?.playerName || null);
          setScenarioIdea(data?.scenarioIdea || null);
          setSelectedTags(data?.selectedTags || []);
        }
      },
      {
        condition: step > 1,
        action: () => setCharactersNames(data?.charactersNames || [])
      },
      {
        condition: step > 2,
        action: () => {
          setPlayerDescription(data?.playerDescription || null);
          setCharactersDescription(data?.charactersDescription || {});
          setCharactersAvatars(data?.charactersAvatars || {});
        }
      },
      {
        condition: step > 3,
        action: () => setSetup(data?.setup || null)
      },
      {
        condition: step > 4,
        action: () => setLocation(data?.location || null)
      },
      {
        condition: step > 5,
        action: () => setInitialMessages(data?.initialMessages || [])
      },
      {
        condition: step > 6,
        action: () => setScenarioName(data?.scenarioName || null)
      }
    ];

    stepSetters
      .filter(item => item.condition)
      .forEach(item => item.action());
  }, []);

  const resetState = useCallback(() => {
    ls.remove('scenario_wizard_data');
    ls.remove('scenario_wizard_step');
    ls.remove('scenario_wizard_pictures_generated');
    setCurrentStep(1);
    setPlayerName(currentUser?.characterName || null);
    setScenarioIdea(null);
    setCharactersNames([]);
    setPlayerDescription(currentUser?.characterDescription || null);
    setCharactersDescription({});
    setCharactersAvatars({});
    setScenarioName(null);
    setSetup(null);
    setLocation(null);
    setInitialMessages([]);
    setSelectedTags([]);
    setPicturesGenerated(0);
  }, [currentUser]);

  return {
    currentStep,
    setCurrentStep,
    playerName,
    setPlayerName,
    scenarioIdea,
    setScenarioIdea,
    charactersNames,
    setCharactersNames,
    playerDescription,
    setPlayerDescription,
    charactersDescription,
    setCharactersDescription,
    charactersAvatars,
    setCharactersAvatars,
    scenarioName,
    setScenarioName,
    setup,
    setSetup,
    location,
    setLocation,
    initialMessages,
    setInitialMessages,
    selectedTags,
    setSelectedTags,
    picturesGenerated,
    setPicturesGenerated,
    saveDataToStorage,
    setDataFromStorage,
    resetState
  };
}; 